import { lazy, type ComponentType } from "react";

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const lazyWithRetry = <T extends { default: ComponentType<any> }>(
  importer: () => Promise<T>,
  retries = 2,
  initialDelayMs = 400,
) =>
  lazy(async () => {
    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await importer();
      } catch (error) {
        lastError = error;

        if (attempt < retries) {
          await sleep(initialDelayMs * (attempt + 1));
        }
      }
    }

    throw lastError;
  });

export const AboutPage = lazyWithRetry(() => import("soapbox/features/about/index.tsx"));
export const EmojiPicker = lazyWithRetry(
  () => import("soapbox/features/emoji/components/emoji-picker.tsx"),
);
export const EmojiPickerModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/emoji-picker-modal.tsx"),
);
export const Notifications = lazyWithRetry(
  () => import("soapbox/features/notifications/index.tsx"),
);
export const LandingTimeline = lazyWithRetry(
  () => import("soapbox/features/landing-timeline/index.tsx"),
);
export const HomeTimeline = lazyWithRetry(
  () => import("soapbox/features/home-timeline/index.tsx"),
);
export const PublicTimeline = lazyWithRetry(
  () => import("soapbox/features/public-timeline/index.tsx"),
);
export const RemoteTimeline = lazyWithRetry(
  () => import("soapbox/features/remote-timeline/index.tsx"),
);
export const CommunityTimeline = lazyWithRetry(
  () => import("soapbox/features/community-timeline/index.tsx"),
);
export const HashtagTimeline = lazyWithRetry(
  () => import("soapbox/features/hashtag-timeline/index.tsx"),
);
export const DirectTimeline = lazyWithRetry(
  () => import("soapbox/features/direct-timeline/index.tsx"),
);
export const Conversations = lazyWithRetry(
  () => import("soapbox/features/conversations/index.tsx"),
);
export const ListTimeline = lazyWithRetry(
  () => import("soapbox/features/list-timeline/index.tsx"),
);
export const Lists = lazyWithRetry(() => import("soapbox/features/lists/index.tsx"));
export const Bookmarks = lazyWithRetry(
  () => import("soapbox/features/bookmarks/index.tsx"),
);
export const Status = lazyWithRetry(() => import("soapbox/features/status/index.tsx"));
export const PinnedStatuses = lazyWithRetry(
  () => import("soapbox/features/pinned-statuses/index.tsx"),
);
export const AccountTimeline = lazyWithRetry(
  () => import("soapbox/features/account-timeline/index.tsx"),
);
export const AccountGallery = lazyWithRetry(
  () => import("soapbox/features/account-gallery/index.tsx"),
);
export const Followers = lazyWithRetry(
  () => import("soapbox/features/followers/index.tsx"),
);
export const Following = lazyWithRetry(
  () => import("soapbox/features/following/index.tsx"),
);
export const FollowRequests = lazyWithRetry(
  () => import("soapbox/features/follow-requests/index.tsx"),
);
export const GenericNotFound = lazyWithRetry(
  () => import("soapbox/features/generic-not-found/index.tsx"),
);
export const FavouritedStatuses = lazyWithRetry(
  () => import("soapbox/features/favourited-statuses/index.tsx"),
);
export const Blocks = lazyWithRetry(() => import("soapbox/features/blocks/index.tsx"));
export const DomainBlocks = lazyWithRetry(
  () => import("soapbox/features/domain-blocks/index.tsx"),
);
export const Mutes = lazyWithRetry(() => import("soapbox/features/mutes/index.tsx"));
export const MuteModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/mute-modal.tsx"),
);
export const Filters = lazyWithRetry(() => import("soapbox/features/filters/index.tsx"));
export const EditFilter = lazyWithRetry(
  () => import("soapbox/features/filters/edit-filter.tsx"),
);
export const ReportModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/report-modal/report-modal.tsx"
    ),
);
export const AccountModerationModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/account-moderation-modal/account-moderation-modal.tsx"
    ),
);
export const MediaGallery = lazyWithRetry(
  () => import("soapbox/components/media-gallery.tsx"),
);
export const Video = lazyWithRetry(() => import("soapbox/features/video/index.tsx"));
export const Audio = lazyWithRetry(() => import("soapbox/features/audio/index.tsx"));
export const MediaModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/media-modal.tsx"),
);
export const VideoModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/video-modal.tsx"),
);
export const BoostModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/boost-modal.tsx"),
);
export const ConfirmationModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/confirmation-modal.tsx"),
);
export const MissingDescriptionModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/missing-description-modal.tsx"
    ),
);
export const ActionsModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/actions-modal.tsx"),
);
export const HotkeysModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/hotkeys-modal.tsx"),
);
export const ComposeModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/compose-modal.tsx"),
);
export const ReplyMentionsModal = lazyWithRetry(
  () =>
    import("soapbox/features/ui/components/modals/reply-mentions-modal.tsx"),
);
export const UnauthorizedModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/unauthorized-modal.tsx"),
);
export const EditFederationModal = lazyWithRetry(
  () =>
    import("soapbox/features/ui/components/modals/edit-federation-modal.tsx"),
);
export const EmbedModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/embed-modal.tsx"),
);
export const ComponentModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/component-modal.tsx"),
);
export const ReblogsModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/reblogs-modal.tsx"),
);
export const FavouritesModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/favourites-modal.tsx"),
);
export const DislikesModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/dislikes-modal.tsx"),
);
export const ReactionsModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/reactions-modal.tsx"),
);
export const MentionsModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/mentions-modal.tsx"),
);
export const LandingPageModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/landing-page-modal.tsx"),
);
export const BirthdaysModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/birthdays-modal.tsx"),
);
export const BirthdayPanel = lazyWithRetry(
  () => import("soapbox/components/birthday-panel.tsx"),
);
export const ListEditor = lazyWithRetry(
  () => import("soapbox/features/list-editor/index.tsx"),
);
export const ListAdder = lazyWithRetry(
  () => import("soapbox/features/list-adder/index.tsx"),
);
export const Search = lazyWithRetry(() => import("soapbox/features/search/index.tsx"));
export const LoginPage = lazyWithRetry(
  () => import("soapbox/features/auth-login/components/login-page.tsx"),
);
export const ExternalLogin = lazyWithRetry(
  () => import("soapbox/features/external-login/index.tsx"),
);
export const LogoutPage = lazyWithRetry(
  () => import("soapbox/features/auth-login/components/logout.tsx"),
);
export const RegistrationPage = lazyWithRetry(
  () => import("soapbox/features/auth-login/components/registration-page.tsx"),
);
export const Settings = lazyWithRetry(
  () => import("soapbox/features/settings/index.tsx"),
);
export const EditProfile = lazyWithRetry(
  () => import("soapbox/features/edit-profile/index.tsx"),
);
export const EditEmail = lazyWithRetry(
  () => import("soapbox/features/edit-email/index.tsx"),
);
export const EmailConfirmation = lazyWithRetry(
  () => import("soapbox/features/email-confirmation/index.tsx"),
);
export const EditPassword = lazyWithRetry(
  () => import("soapbox/features/edit-password/index.tsx"),
);
export const DeleteAccount = lazyWithRetry(
  () => import("soapbox/features/delete-account/index.tsx"),
);
export const SoapboxConfig = lazyWithRetry(
  () => import("soapbox/features/soapbox-config/index.tsx"),
);
export const ExportData = lazyWithRetry(
  () => import("soapbox/features/export-data/index.tsx"),
);
export const ImportData = lazyWithRetry(
  () => import("soapbox/features/import-data/index.tsx"),
);
export const Backups = lazyWithRetry(() => import("soapbox/features/backups/index.tsx"));
export const PasswordReset = lazyWithRetry(
  () => import("soapbox/features/auth-login/components/password-reset.tsx"),
);
export const PasswordResetConfirm = lazyWithRetry(
  () =>
    import("soapbox/features/auth-login/components/password-reset-confirm.tsx"),
);
export const MfaForm = lazyWithRetry(
  () => import("soapbox/features/security/mfa-form.tsx"),
);
export const ChatIndex = lazyWithRetry(() => import("soapbox/features/chats/index.tsx"));
export const ChatWidget = lazyWithRetry(
  () => import("soapbox/features/chats/components/chat-widget/chat-widget.tsx"),
);
export const ServerInfo = lazyWithRetry(
  () => import("soapbox/features/server-info/index.tsx"),
);
export const Dashboard = lazyWithRetry(() => import("soapbox/features/admin/index.tsx"));
export const ModerationLog = lazyWithRetry(
  () => import("soapbox/features/admin/moderation-log.tsx"),
);
export const ThemeEditor = lazyWithRetry(
  () => import("soapbox/features/theme-editor/index.tsx"),
);
export const UserPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/user-panel.tsx"),
);
export const PromoPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/promo-panel.tsx"),
);
export const AdsPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/ads-panel.tsx"),
);
export const SignUpPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/panels/sign-up-panel.tsx"),
);
export const CtaBanner = lazyWithRetry(
  () => import("soapbox/features/ui/components/cta-banner.tsx"),
);
export const FundingPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/funding-panel.tsx"),
);
export const TrendsPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/trends-panel.tsx"),
);
export const ProfileInfoPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/profile-info-panel.tsx"),
);
export const ProfileMediaPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/profile-media-panel.tsx"),
);
export const ProfileFieldsPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/profile-fields-panel.tsx"),
);
export const PinnedAccountsPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/pinned-accounts-panel.tsx"),
);
export const InstanceInfoPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/instance-info-panel.tsx"),
);
export const InstanceModerationPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/instance-moderation-panel.tsx"),
);
export const LatestAccountsPanel = lazyWithRetry(
  () => import("soapbox/features/admin/components/latest-accounts-panel.tsx"),
);
export const SidebarMenu = lazyWithRetry(
  () => import("soapbox/components/sidebar-menu.tsx"),
);
export const ModalContainer = lazyWithRetry(
  () => import("soapbox/features/ui/containers/modal-container.ts"),
);
export const ProfileHoverCard = lazyWithRetry(
  () => import("soapbox/components/profile-hover-card.tsx"),
);
export const StatusHoverCard = lazyWithRetry(
  () => import("soapbox/components/status-hover-card.tsx"),
);
export const CryptoDonate = lazyWithRetry(
  () => import("soapbox/features/crypto-donate/index.tsx"),
);
export const CryptoDonatePanel = lazyWithRetry(
  () =>
    import("soapbox/features/crypto-donate/components/crypto-donate-panel.tsx"),
);
export const CryptoAddress = lazyWithRetry(
  () => import("soapbox/features/crypto-donate/components/crypto-address.tsx"),
);
export const CryptoDonateModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/crypto-donate-modal.tsx"),
);
export const LightningAddress = lazyWithRetry(
  () =>
    import("soapbox/features/crypto-donate/components/lightning-address.tsx"),
);
export const ScheduledStatuses = lazyWithRetry(
  () => import("soapbox/features/scheduled-statuses/index.tsx"),
);
export const UserIndex = lazyWithRetry(
  () => import("soapbox/features/admin/user-index.tsx"),
);
export const FederationRestrictions = lazyWithRetry(
  () => import("soapbox/features/federation-restrictions/index.tsx"),
);
export const Aliases = lazyWithRetry(() => import("soapbox/features/aliases/index.tsx"));
export const Migration = lazyWithRetry(
  () => import("soapbox/features/migration/index.tsx"),
);
export const WhoToFollowPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/who-to-follow-panel.tsx"),
);
export const FollowRecommendations = lazyWithRetry(
  () => import("soapbox/features/follow-recommendations/index.tsx"),
);
export const Directory = lazyWithRetry(
  () => import("soapbox/features/directory/index.tsx"),
);
export const RegisterInvite = lazyWithRetry(
  () => import("soapbox/features/register-invite/index.tsx"),
);
export const Share = lazyWithRetry(() => import("soapbox/features/share/index.tsx"));
export const NewStatus = lazyWithRetry(
  () => import("soapbox/features/new-status/index.tsx"),
);
export const IntentionalError = lazyWithRetry(
  () => import("soapbox/features/intentional-error/index.tsx"),
);
export const Developers = lazyWithRetry(
  () => import("soapbox/features/developers/index.tsx"),
);
export const CreateApp = lazyWithRetry(
  () => import("soapbox/features/developers/apps/create.tsx"),
);
export const SettingsStore = lazyWithRetry(
  () => import("soapbox/features/developers/settings-store.tsx"),
);
export const TestTimeline = lazyWithRetry(
  () => import("soapbox/features/test-timeline/index.tsx"),
);
export const ServiceWorkerInfo = lazyWithRetry(
  () => import("soapbox/features/developers/service-worker-info.tsx"),
);
export const CompareHistoryModal = lazyWithRetry(
  () =>
    import("soapbox/features/ui/components/modals/compare-history-modal.tsx"),
);
export const AuthTokenList = lazyWithRetry(
  () => import("soapbox/features/auth-token-list/index.tsx"),
);
export const FamiliarFollowersModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/familiar-followers-modal.tsx"
    ),
);
export const AnnouncementsPanel = lazyWithRetry(
  () => import("soapbox/components/announcements/announcements-panel.tsx"),
);
export const Quotes = lazyWithRetry(() => import("soapbox/features/quotes/index.tsx"));
export const ComposeEventModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/compose-event-modal/compose-event-modal.tsx"
    ),
);
export const JoinEventModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/join-event-modal.tsx"),
);
export const EventHeader = lazyWithRetry(
  () => import("soapbox/features/event/components/event-header.tsx"),
);
export const EventInformation = lazyWithRetry(
  () => import("soapbox/features/event/event-information.tsx"),
);
export const EventDiscussion = lazyWithRetry(
  () => import("soapbox/features/event/event-discussion.tsx"),
);
export const EventMapModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/event-map-modal.tsx"),
);
export const EventParticipantsModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/event-participants-modal.tsx"
    ),
);
export const Events = lazyWithRetry(() => import("soapbox/features/events/index.tsx"));
export const Groups = lazyWithRetry(() => import("soapbox/features/groups/index.tsx"));
export const GroupsDiscover = lazyWithRetry(
  () => import("soapbox/features/groups/discover.tsx"),
);
export const GroupsPopular = lazyWithRetry(
  () => import("soapbox/features/groups/popular.tsx"),
);
export const GroupsSuggested = lazyWithRetry(
  () => import("soapbox/features/groups/suggested.tsx"),
);
export const GroupsTag = lazyWithRetry(() => import("soapbox/features/groups/tag.tsx"));
export const GroupsTags = lazyWithRetry(
  () => import("soapbox/features/groups/tags.tsx"),
);
export const PendingGroupRequests = lazyWithRetry(
  () => import("soapbox/features/groups/pending-requests.tsx"),
);
export const GroupMembers = lazyWithRetry(
  () => import("soapbox/features/group/group-members.tsx"),
);
export const GroupTags = lazyWithRetry(
  () => import("soapbox/features/group/group-tags.tsx"),
);
export const GroupTagTimeline = lazyWithRetry(
  () => import("soapbox/features/group/group-tag-timeline.tsx"),
);
export const GroupTimeline = lazyWithRetry(
  () => import("soapbox/features/group/group-timeline.tsx"),
);
export const ManageGroup = lazyWithRetry(
  () => import("soapbox/features/group/manage-group.tsx"),
);
export const EditGroup = lazyWithRetry(
  () => import("soapbox/features/group/edit-group.tsx"),
);
export const GroupBlockedMembers = lazyWithRetry(
  () => import("soapbox/features/group/group-blocked-members.tsx"),
);
export const GroupMembershipRequests = lazyWithRetry(
  () => import("soapbox/features/group/group-membership-requests.tsx"),
);
export const GroupGallery = lazyWithRetry(
  () => import("soapbox/features/group/group-gallery.tsx"),
);
export const CreateGroupModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/manage-group-modal/create-group-modal.tsx"
    ),
);
export const NewGroupPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/panels/new-group-panel.tsx"),
);
export const MyGroupsPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/panels/my-groups-panel.tsx"),
);
export const SuggestedGroupsPanel = lazyWithRetry(
  () =>
    import("soapbox/features/ui/components/panels/suggested-groups-panel.tsx"),
);
export const GroupMediaPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/group-media-panel.tsx"),
);
export const NewEventPanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/panels/new-event-panel.tsx"),
);
export const Announcements = lazyWithRetry(
  () => import("soapbox/features/admin/announcements.tsx"),
);
export const EditAnnouncementModal = lazyWithRetry(
  () =>
    import("soapbox/features/ui/components/modals/edit-announcement-modal.tsx"),
);
export const FollowedTags = lazyWithRetry(
  () => import("soapbox/features/followed-tags/index.tsx"),
);
export const AccountNotePanel = lazyWithRetry(
  () => import("soapbox/features/ui/components/panels/account-note-panel.tsx"),
);
export const ComposeEditor = lazyWithRetry(
  () => import("soapbox/features/compose/editor/index.tsx"),
);
export const OnboardingFlowModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/onboarding-flow-modal/onboarding-flow-modal.tsx"
    ),
);
export const NostrSignupModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/nostr-signup-modal/nostr-signup-modal.tsx"
    ),
);
export const NostrLoginModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/nostr-login-modal/nostr-login-modal.tsx"
    ),
);
export const BookmarkFolders = lazyWithRetry(
  () => import("soapbox/features/bookmark-folders/index.tsx"),
);
export const EditBookmarkFolderModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/edit-bookmark-folder-modal.tsx"
    ),
);
export const SelectBookmarkFolderModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/select-bookmark-folder-modal.tsx"
    ),
);
export const EditIdentity = lazyWithRetry(
  () => import("soapbox/features/edit-identity/index.tsx"),
);
export const Domains = lazyWithRetry(() => import("soapbox/features/admin/domains.tsx"));
export const EditDomainModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/edit-domain-modal.tsx"),
);
export const NostrRelays = lazyWithRetry(
  () => import("soapbox/features/nostr-relays/index.tsx"),
);
export const Bech32Redirect = lazyWithRetry(
  () => import("soapbox/features/nostr/Bech32Redirect.tsx"),
);
export const ManageZapSplit = lazyWithRetry(
  () => import("soapbox/features/admin/manage-zap-split.tsx"),
);
export const Relays = lazyWithRetry(() => import("soapbox/features/admin/relays.tsx"));
export const Rules = lazyWithRetry(() => import("soapbox/features/admin/rules.tsx"));
export const EditRuleModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/edit-rule-modal.tsx"),
);
export const AdminNostrRelays = lazyWithRetry(
  () => import("soapbox/features/admin/nostr-relays.tsx"),
);
export const ZapPayRequestModal = lazyWithRetry(
  () =>
    import("soapbox/features/ui/components/modals/zap-pay-request-modal.tsx"),
);
export const ZapInvoiceModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/zap-invoice.tsx"),
);
export const ZapsModal = lazyWithRetry(
  () => import("soapbox/features/ui/components/modals/zaps-modal.tsx"),
);
export const ZapSplitModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/zap-split/zap-split-modal.tsx"
    ),
);
export const CaptchaModal = lazyWithRetry(
  () =>
    import(
      "soapbox/features/ui/components/modals/captcha-modal/captcha-modal.tsx"
    ),
);
export const NostrBunkerLogin = lazyWithRetry(
  () => import("soapbox/features/nostr/nostr-bunker-login.tsx"),
);
